/**
 * Hook partagé : centrage carte pour éditeurs équipements / pitches / relais.
 */

import { useMemo } from 'react'
import { useOrganization } from '../hooks/useOrganization'
import { useOrganizationLocations } from '../hooks/useOrganizationLocations'
import type { MapPickerBounds } from '../components/MapPicker'

const QUEBEC_FALLBACK: [number, number] = [46.3432, -72.5793]

export function useOrgSiteMapView(organizationId: number, defaultCenter?: [number, number]) {
  const { data: organization } = useOrganization(organizationId)
  const { data: locations = [] } = useOrganizationLocations(organizationId)

  const siteView = useMemo(() => {
    const bbox = organization?.boundary_bbox
    if (
      bbox &&
      Number.isFinite(bbox.west) &&
      Number.isFinite(bbox.south) &&
      Number.isFinite(bbox.east) &&
      Number.isFinite(bbox.north) &&
      bbox.west < bbox.east &&
      bbox.south < bbox.north
    ) {
      const center: [number, number] = [
        (bbox.south + bbox.north) / 2,
        (bbox.west + bbox.east) / 2,
      ]
      return { center, zoom: 14, fitBounds: bbox as MapPickerBounds, ready: true }
    }

    const orgCoords = organization?.coordinates
    if (
      orgCoords &&
      Number.isFinite(orgCoords.lat) &&
      Number.isFinite(orgCoords.lng) &&
      !(orgCoords.lat === 0 && orgCoords.lng === 0)
    ) {
      return {
        center: [orgCoords.lat, orgCoords.lng] as [number, number],
        zoom: 15,
        fitBounds: null as MapPickerBounds | null,
        ready: true,
      }
    }

    if (defaultCenter) {
      return { center: defaultCenter, zoom: 14, fitBounds: null as MapPickerBounds | null, ready: true }
    }

    const withCoords = locations.filter(
      (l) =>
        l.coordinates &&
        Number.isFinite(l.coordinates.lat) &&
        Number.isFinite(l.coordinates.lng) &&
        !(l.coordinates.lat === 0 && l.coordinates.lng === 0),
    )
    if (withCoords.length > 0) {
      const lat =
        withCoords.reduce((s, l) => s + l.coordinates.lat, 0) / withCoords.length
      const lng =
        withCoords.reduce((s, l) => s + l.coordinates.lng, 0) / withCoords.length
      return {
        center: [lat, lng] as [number, number],
        zoom: withCoords.length === 1 ? 15 : 13,
        fitBounds: null as MapPickerBounds | null,
        ready: true,
      }
    }

    return {
      center: QUEBEC_FALLBACK,
      zoom: 5,
      fitBounds: null as MapPickerBounds | null,
      ready: false,
    }
  }, [organization, locations, defaultCenter])

  const mapKey = siteView.ready
    ? `site-${siteView.center[0].toFixed(5)}-${siteView.center[1].toFixed(5)}-${siteView.zoom}`
    : 'pending'

  const campings = useMemo(
    () => locations.filter((l) => (l.type || '').toLowerCase() === 'camping'),
    [locations],
  )
  const refuges = useMemo(
    () => locations.filter((l) => (l.type || '').toLowerCase() === 'refuge'),
    [locations],
  )

  return { siteView, mapKey, campings, refuges, locations }
}
